import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { formatDate, formatNumber } from "./utils";

export function exportToPDF(data, columns, title, totals = null, subtitle = null) {
  const doc = new jsPDF();
  
  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("AP DEPURATORE", 14, 20);
  
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(title, 14, 30);
  
  if (subtitle) {
    doc.setFontSize(10);
    doc.text(subtitle, 14, 38);
  }
  
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generato il: ${formatDate(new Date().toISOString())}`, 14, subtitle ? 46 : 38);
  
  // Table
  const tableData = data.map((row) =>
    columns.map((col) => {
      const value = row[col.key];
      if (col.type === "date") return formatDate(value);
      if (col.type === "number") return formatNumber(value) + " kg";
      return value || "-";
    })
  );

  autoTable(doc, {
    head: [columns.map((col) => col.label)],
    body: tableData,
    startY: subtitle ? 52 : 45,
    styles: {
      fontSize: 9,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
  });

  // Totals
  if (totals) {
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0);
    
    let y = finalY;
    Object.entries(totals).forEach(([label, value]) => {
      doc.text(`${label}: ${formatNumber(value)} kg`, 14, y);
      y += 7;
    });
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Pagina ${i} di ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: "center" }
    );
  }

  doc.save(`${title.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
}

export function exportPartitaToPDF(partita, movimenti, totals) {
  const doc = new jsPDF();
  
  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("AP DEPURATORE", 14, 20);
  
  doc.setFontSize(14);
  doc.text(`Dettaglio Partita: ${partita.codice_partita}`, 14, 32);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100);
  doc.text(`Generato il: ${formatDate(new Date().toISOString())}`, 14, 42);
  
  // Partita Info
  doc.setTextColor(0);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Informazioni Partita", 14, 55);
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const infoY = 63;
  doc.text(`Fornitore: ${partita.fornitore}`, 14, infoY);
  doc.text(`Prodotto: ${partita.prodotto}`, 14, infoY + 7);
  doc.text(`Data Entrata: ${formatDate(partita.data_entrata)}`, 14, infoY + 14);
  doc.text(`Quantità Iniziale: ${formatNumber(partita.quantita_iniziale)} kg`, 14, infoY + 21);
  doc.text(`Quantità Disponibile: ${formatNumber(partita.quantita_disponibile)} kg`, 14, infoY + 28);
  
  // Summary Box
  doc.setFillColor(248, 250, 252);
  doc.rect(120, 55, 75, 45, "F");
  doc.setFont("helvetica", "bold");
  doc.text("Riepilogo", 125, 63);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(16, 185, 129);
  doc.text(`Entrate: +${formatNumber(totals.totale_entrate)} kg`, 125, 73);
  doc.setTextColor(239, 68, 68);
  doc.text(`Uscite: -${formatNumber(totals.totale_uscite)} kg`, 125, 81);
  doc.setTextColor(217, 119, 6);
  doc.text(`Scarto: -${formatNumber(totals.totale_scarto || 0)} kg`, 125, 89);
  doc.setTextColor(0);
  doc.setFont("helvetica", "bold");
  doc.text(`Saldo: ${formatNumber(totals.saldo)} kg`, 125, 97);
  
  // Movements Table
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Storico Movimenti", 14, 110);
  
  const columns = [
    { key: "tipo", label: "Tipo" },
    { key: "data", label: "Data" },
    { key: "soggetto", label: "Fornitore/Cliente" },
    { key: "quantita", label: "Quantità" },
    { key: "note", label: "Note" },
  ];
  
  const tableData = movimenti.map((mov) => [
    mov.tipo === "entrata" ? "ENTRATA" : "USCITA",
    formatDate(mov.data),
    mov.tipo === "entrata" ? (mov.fornitore || "-") : (mov.cliente || "-"),
    `${mov.tipo === "entrata" ? "+" : "-"}${formatNumber(mov.quantita)} kg`,
    mov.note || "-",
  ]);

  autoTable(doc, {
    head: [columns.map((col) => col.label)],
    body: tableData,
    startY: 110,
    styles: {
      fontSize: 9,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    columnStyles: {
      0: { cellWidth: 25 },
      3: { cellWidth: 30 },
    },
  });

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Pagina ${i} di ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: "center" }
    );
  }

  doc.save(`Partita_${partita.codice_partita}_${new Date().toISOString().split("T")[0]}.pdf`);
}

export function exportToExcel(data, columns, title) {
  const worksheetData = [
    columns.map((col) => col.label),
    ...data.map((row) =>
      columns.map((col) => {
        const value = row[col.key];
        if (col.type === "date") return formatDate(value);
        if (col.type === "number") return parseFloat(value) || 0;
        return value || "";
      })
    ),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  
  // Set column widths
  worksheet["!cols"] = columns.map(() => ({ wch: 20 }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Dati");

  XLSX.writeFile(workbook, `${title.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.xlsx`);
}

export function exportReportToPDF(data, isGrouped, totals, filterDescription = null, totaliPerProdotto = null) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  // Colors
  const primaryColor = [234, 88, 12]; // orange-600
  const darkColor = [15, 23, 42]; // slate-900
  const lightBg = [248, 250, 252]; // slate-50
  const mediumBg = [241, 245, 249]; // slate-100
  const borderColor = [203, 213, 225]; // slate-300
  const successColor = [16, 185, 129]; // emerald-500
  const infoColor = [59, 130, 246]; // blue-500
  const warningColor = [217, 119, 6]; // amber-600

  // ==================== HEADER ====================
  // Header background
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 28, "F");
  
  // Company name
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("AP DEPURATORE", 14, 16);
  
  // Report title
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text("Report Movimenti", pageWidth - 14, 12, { align: "right" });
  doc.setFontSize(9);
  doc.text(formatDate(new Date().toISOString()), pageWidth - 14, 20, { align: "right" });

  let currentY = 38;
  
  // Filter box if present
  if (filterDescription) {
    doc.setFillColor(...mediumBg);
    doc.roundedRect(14, currentY - 4, pageWidth - 28, 14, 2, 2, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkColor);
    doc.text(`Filtri applicati: ${filterDescription}`, 18, currentY + 4);
    currentY += 18;
  }

  // ==================== CONTENT ====================
  if (isGrouped && typeof data === 'object' && !Array.isArray(data)) {
    const codici = Object.keys(data).sort();
    
    for (const codice of codici) {
      const group = data[codice];
      const scarto = group.scarto || 0;
      const saldo = group.entrate - group.uscite - scarto;
      const hasGrossa = (group.grossa || 0) > 0;
      const hasMedia = (group.media || 0) > 0;
      const hasBolla = (group.bolla || 0) > 0;
      const hasPezzature = hasGrossa || hasMedia || hasBolla;
      
      if (currentY > 230) {
        doc.addPage();
        currentY = 20;
      }
      
      // Partita card header
      doc.setFillColor(...darkColor);
      doc.roundedRect(14, currentY, pageWidth - 28, 10, 2, 2, "F");
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255);
      doc.text(`PARTITA: ${codice}`, 18, currentY + 7);
      
      // Prodotto name on the right
      if (group.movimenti && group.movimenti[0]?.prodotto) {
        doc.setFont("helvetica", "normal");
        doc.text(group.movimenti[0].prodotto.toUpperCase(), pageWidth - 18, currentY + 7, { align: "right" });
      }
      
      currentY += 14;
      
      // Summary row with colored badges
      doc.setFontSize(9);
      const badgeY = currentY;
      const badgeHeight = 8;
      
      // Entrate badge
      doc.setFillColor(209, 250, 229); // emerald-100
      doc.roundedRect(14, badgeY, 40, badgeHeight, 1, 1, "F");
      doc.setTextColor(...successColor);
      doc.setFont("helvetica", "bold");
      doc.text(`+${formatNumber(group.entrate)} kg`, 34, badgeY + 5.5, { align: "center" });
      
      // Uscite badge
      doc.setFillColor(219, 234, 254); // blue-100
      doc.roundedRect(58, badgeY, 40, badgeHeight, 1, 1, "F");
      doc.setTextColor(...infoColor);
      doc.text(`-${formatNumber(group.uscite)} kg`, 78, badgeY + 5.5, { align: "center" });
      
      // Scarto badge
      doc.setFillColor(254, 243, 199); // amber-100
      doc.roundedRect(102, badgeY, 40, badgeHeight, 1, 1, "F");
      doc.setTextColor(...warningColor);
      doc.text(`-${formatNumber(scarto)} kg`, 122, badgeY + 5.5, { align: "center" });
      
      // Saldo badge
      doc.setFillColor(...primaryColor);
      doc.roundedRect(146, badgeY, 46, badgeHeight, 1, 1, "F");
      doc.setTextColor(255);
      doc.text(`SALDO: ${formatNumber(saldo)} kg`, 169, badgeY + 5.5, { align: "center" });
      
      currentY += 12;
      
      // Movements table
      let tableHead, tableData;
      
      if (hasPezzature) {
        const headers = ["Data", "Tipo", "Fornitore", "Cliente"];
        if (hasGrossa) headers.push("Grossa");
        if (hasMedia) headers.push("Media");
        if (hasBolla) headers.push("Bolla");
        headers.push("Totale");
        
        tableHead = [headers];
        tableData = group.movimenti.map((mov) => {
          const isScarto = mov.tipo === "uscita" && (mov.cliente || "").toUpperCase() === "SCARTO";
          const row = [
            formatDate(mov.data),
            mov.tipo === "entrata" ? "ENTRATA" : isScarto ? "SCARTO" : "USCITA",
            mov.fornitore || mov.fornitore_partita || "-",
            mov.cliente || "-",
          ];
          if (hasGrossa) {
            row.push(mov.tipo === "entrata" && (mov.quantita_grossa || 0) > 0 ? `${formatNumber(mov.quantita_grossa)}` : "-");
          }
          if (hasMedia) {
            row.push(mov.tipo === "entrata" && (mov.quantita_media || 0) > 0 ? `${formatNumber(mov.quantita_media)}` : "-");
          }
          if (hasBolla) {
            row.push(mov.tipo === "entrata" && (mov.quantita_bolla || 0) > 0 ? `${formatNumber(mov.quantita_bolla)}` : "-");
          }
          row.push(`${mov.tipo === "entrata" ? "+" : "-"}${formatNumber(mov.quantita)}`);
          return row;
        });
      } else {
        tableHead = [["Data", "Tipo", "Fornitore", "Cliente", "Quantità"]];
        tableData = group.movimenti.map((mov) => {
          const isScarto = mov.tipo === "uscita" && (mov.cliente || "").toUpperCase() === "SCARTO";
          return [
            formatDate(mov.data),
            mov.tipo === "entrata" ? "ENTRATA" : isScarto ? "SCARTO" : "USCITA",
            mov.fornitore || mov.fornitore_partita || "-",
            mov.cliente || "-",
            `${mov.tipo === "entrata" ? "+" : "-"}${formatNumber(mov.quantita)}`,
          ];
        });
      }

      autoTable(doc, {
        head: tableHead,
        body: tableData,
        startY: currentY,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: darkColor, textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: lightBg },
        margin: { left: 14, right: 14 },
      });
      
      currentY = doc.lastAutoTable.finalY + 12;
    }
  } else {
    // Flat view
    const movimenti = Array.isArray(data) ? data : [];
    
    autoTable(doc, {
      head: [["Codice", "Data", "Tipo", "Fornitore", "Cliente", "Prodotto", "Quantità"]],
      body: movimenti.map((mov) => [
        mov.codice_partita,
        formatDate(mov.data),
        mov.tipo === "entrata" ? "ENTRATA" : (mov.cliente || "").toUpperCase() === "SCARTO" ? "SCARTO" : "USCITA",
        mov.fornitore || mov.fornitore_partita || "-",
        mov.cliente || "-",
        mov.prodotto || "-",
        `${mov.tipo === "entrata" ? "+" : "-"}${formatNumber(mov.quantita)} kg`,
      ]),
      startY: currentY,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: darkColor, textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: lightBg },
      margin: { left: 14, right: 14 },
    });
    
    currentY = doc.lastAutoTable.finalY + 12;
  }
  
  // ==================== RIEPILOGO PER PRODOTTO (include tutto) ====================
  if (totaliPerProdotto && Object.keys(totaliPerProdotto).length > 0) {
    const prodottiConDati = Object.entries(totaliPerProdotto).filter(
      ([_, dati]) => dati.grossa > 0 || dati.media > 0 || dati.bolla > 0 || dati.entrate > 0 || dati.uscite > 0
    );
    
    if (prodottiConDati.length > 0) {
      // New page if needed
      if (currentY > 160) {
        doc.addPage();
        currentY = 20;
      }
      
      // Section title
      doc.setFillColor(...primaryColor);
      doc.roundedRect(14, currentY, pageWidth - 28, 10, 2, 2, "F");
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255);
      doc.text("RIEPILOGO PER PRODOTTO", pageWidth / 2, currentY + 7, { align: "center" });
      currentY += 18;
      
      // Create a box for each product
      for (const [nomeProdotto, dati] of prodottiConDati) {
        if (currentY > 200) {
          doc.addPage();
          currentY = 20;
        }
        
        const percentuale = dati.percentuale || 5;
        const moltiplicatore = (100 - percentuale) / 100;
        const grossaPiuBolla = (dati.grossa || 0) + (dati.bolla || 0);
        const totGr = grossaPiuBolla * moltiplicatore;
        const totMedia = (dati.media || 0) * moltiplicatore;
        const totPag = totGr - (dati.bolla || 0);
        
        const entrate = dati.entrate || 0;
        const uscite = dati.uscite || 0;
        const scarto = dati.scarto || 0;
        const saldo = entrate - uscite - scarto;
        
        const boxHeight = 75;
        
        // Product box
        doc.setFillColor(...lightBg);
        doc.roundedRect(14, currentY, pageWidth - 28, boxHeight, 3, 3, "F");
        doc.setDrawColor(...borderColor);
        doc.roundedRect(14, currentY, pageWidth - 28, boxHeight, 3, 3, "S");
        
        // Product name header
        doc.setFillColor(...darkColor);
        doc.roundedRect(14, currentY, pageWidth - 28, 12, 3, 3, "F");
        doc.rect(14, currentY + 6, pageWidth - 28, 6, "F");
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255);
        doc.text(`${nomeProdotto.toUpperCase()}`, 20, currentY + 8);
        doc.setFontSize(9);
        doc.text(`Sconto: ${percentuale}%`, pageWidth - 20, currentY + 8, { align: "right" });
        
        // Content - Three columns
        let lineY = currentY + 20;
        doc.setTextColor(...darkColor);
        
        const col1X = 20;
        const col2X = 75;
        const col3X = 140;
        
        // Column 1 - Movimenti
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text("MOVIMENTI", col1X, lineY);
        doc.setFont("helvetica", "normal");
        lineY += 6;
        doc.setTextColor(...successColor);
        doc.text(`Entrate: +${formatNumber(entrate)} kg`, col1X, lineY);
        lineY += 5;
        doc.setTextColor(...infoColor);
        doc.text(`Uscite: -${formatNumber(uscite)} kg`, col1X, lineY);
        lineY += 5;
        doc.setTextColor(...warningColor);
        doc.text(`Scarto: -${formatNumber(scarto)} kg`, col1X, lineY);
        lineY += 5;
        doc.setTextColor(...darkColor);
        doc.setFont("helvetica", "bold");
        doc.text(`Saldo: ${formatNumber(saldo)} kg`, col1X, lineY);
        
        // Column 2 - Pezzature originali
        lineY = currentY + 20;
        doc.setTextColor(...darkColor);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text("PEZZATURE", col2X, lineY);
        doc.setFont("helvetica", "normal");
        lineY += 6;
        doc.text(`Grossa: ${formatNumber(dati.grossa || 0)} kg`, col2X, lineY);
        lineY += 5;
        doc.text(`Media: ${formatNumber(dati.media || 0)} kg`, col2X, lineY);
        lineY += 5;
        doc.text(`Bolla: ${formatNumber(dati.bolla || 0)} kg`, col2X, lineY);
        
        // Column 3 - Calcolo netto
        lineY = currentY + 20;
        doc.setFont("helvetica", "bold");
        doc.text(`CALCOLO NETTO (-${percentuale}%)`, col3X, lineY);
        doc.setFont("helvetica", "normal");
        lineY += 6;
        doc.text(`TOT.GR/${percentuale} = ${formatNumber(totGr)} kg`, col3X, lineY);
        lineY += 5;
        doc.text(`TOT.MEDIA/${percentuale} = ${formatNumber(totMedia)} kg`, col3X, lineY);
        lineY += 5;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...primaryColor);
        doc.text(`TOT.PAG = ${formatNumber(totPag)} kg`, col3X, lineY);
        
        currentY += boxHeight + 8;
      }
    }
  }

  // ==================== FOOTER ====================
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    // Footer line
    doc.setDrawColor(...borderColor);
    doc.line(14, doc.internal.pageSize.height - 15, pageWidth - 14, doc.internal.pageSize.height - 15);
    // Page number
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Pagina ${i} di ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.height - 8,
      { align: "center" }
    );
    // Company footer
    doc.text("AP DEPURATORE - Sistema di Gestione", 14, doc.internal.pageSize.height - 8);
  }

  doc.save(`Report_Movimenti_${new Date().toISOString().split("T")[0]}.pdf`);
}


// Export list of partite to PDF
export function exportPartiteListToPDF(partite, statusLabel = "Tutte") {
  const doc = new jsPDF();
  
  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("AP DEPURATORE", 14, 20);
  
  doc.setFontSize(14);
  doc.text("Elenco Partite", 14, 30);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100);
  doc.text(`Filtro: ${statusLabel} | Totale: ${partite.length} partite`, 14, 38);
  doc.text(`Generato il: ${formatDate(new Date().toISOString())}`, 14, 45);
  
  // Calculate totals
  const totaleIniziale = partite.reduce((sum, p) => sum + (p.quantita_iniziale || 0), 0);
  const totaleDisponibile = partite.reduce((sum, p) => sum + (p.quantita_disponibile || 0), 0);
  const totaleScarto = partite.reduce((sum, p) => sum + (p.scarto || 0), 0);
  
  // Summary box
  doc.setFillColor(248, 250, 252);
  doc.rect(130, 20, 65, 30, "F");
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0);
  doc.setFontSize(9);
  doc.text("Riepilogo", 135, 27);
  doc.setFont("helvetica", "normal");
  doc.text(`Iniziale: ${formatNumber(totaleIniziale)} kg`, 135, 35);
  doc.text(`Disponibile: ${formatNumber(totaleDisponibile)} kg`, 135, 42);
  doc.text(`Scarto: ${formatNumber(totaleScarto)} kg`, 135, 49);
  
  // Table
  const tableData = partite.map((p) => [
    p.codice_partita,
    formatDate(p.data_entrata),
    p.fornitore,
    p.prodotto,
    formatNumber(p.quantita_iniziale) + " kg",
    formatNumber(p.quantita_disponibile) + " kg",
    formatNumber(p.scarto || 0) + " kg",
    p.quantita_disponibile > 0 ? "Attiva" : "Chiusa"
  ]);
  
  autoTable(doc, {
    startY: 55,
    head: [["Codice", "Data", "Fornitore", "Prodotto", "Iniziale", "Disponibile", "Scarto", "Stato"]],
    body: tableData,
    theme: "striped",
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontSize: 8,
      fontStyle: "bold",
    },
    bodyStyles: {
      fontSize: 8,
    },
    columnStyles: {
      0: { fontStyle: "bold" },
      4: { halign: "right" },
      5: { halign: "right" },
      6: { halign: "right" },
      7: { halign: "center" },
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    didParseCell: function(data) {
      // Color the status column
      if (data.column.index === 7 && data.section === 'body') {
        if (data.cell.raw === "Attiva") {
          data.cell.styles.textColor = [16, 185, 129];
          data.cell.styles.fontStyle = "bold";
        } else {
          data.cell.styles.textColor = [100, 116, 139];
        }
      }
      // Color scarto column if > 0
      if (data.column.index === 6 && data.section === 'body') {
        const value = parseFloat(data.cell.raw);
        if (value > 0) {
          data.cell.styles.textColor = [217, 119, 6];
        }
      }
    }
  });
  
  // Footer with page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Pagina ${i} di ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: "center" }
    );
  }
  
  doc.save(`Partite_${statusLabel}_${new Date().toISOString().split("T")[0]}.pdf`);
}
